import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as Lucide from 'lucide-react';
import { useSelector } from 'react-redux';
import { RootState, playSound, SharedSoundEffect } from '@mental-mint/shared-ui';
import { getEmbeddingsManagerInstance } from '../../WordDriftUtils';
import type { GameWord } from '../../Utils/GameTypes';
import { logger } from '@mental-mint/shared-utils';
import noChangeSound from '../../Assets/Sounds/word-drift-sfx-no-change.mp3';
import warmerSound from '../../Assets/Sounds/word-drift-sfx-warmer.mp3';
import colderSound from '../../Assets/Sounds/word-drift-sfx-colder.mp3';

/**
 * Feedback types for move feedback modal
 */
export enum MoveFeedbackType {
  HOTTER = 'hotter',
  WARMER = 'warmer',
  COLDER = 'colder',
  NO_CHANGE = 'no change',
  DEAD_END = 'dead end',
  HINT = 'hint'
}

// Helper component to format time display for hint countdown
const TimeCountdown: React.FC<{ start: number; end: number; duration: number }> = ({ start, end, duration }) => {
  const [currentValue, setCurrentValue] = useState(start);
  
  useEffect(() => {
    // Reset to start value when props change
    setCurrentValue(start);
    
    const startTime = Date.now();
    const diff = end - start;
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / (duration * 1000), 1);
      const value = start + (diff * progress);
      setCurrentValue(value);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCurrentValue(end);
      }
    };
    
    animate();
  }, [start, end, duration]);
  
  const totalSeconds = Math.floor(currentValue);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  
  return (
    <span style={{ fontSize: '1.5rem' }}>
      {minutes}:{seconds.toString().padStart(2, '0')}
    </span>
  );
};

interface MoveFeedbackProps {
  word1Lemmatized: string | null;
  word2Lemmatized: string | null;
  word1BfsDistance: number | null | undefined;
  word2BfsDistance: number | null | undefined;
  goalWord: string | null;
  startWord: string | null;
  optimalPath: GameWord[] | undefined;
  timeAfter?: number | null; // Time remaining after hint (for countdown animation - for hint feedback only)
  onComplete?: () => void;
}

export const MoveFeedback: React.FC<MoveFeedbackProps> = ({ 
  word1Lemmatized, 
  word2Lemmatized, 
  word1BfsDistance, 
  word2BfsDistance, 
  goalWord, 
  startWord, 
  optimalPath,
  timeAfter, 
  onComplete 
}) => {
  // Get game color from Redux
  const { currentGame } = useSelector((state: RootState) => state.gameSession);
  const game = currentGame!;
  const gameColor = game.colorPrimary;
  
  // Calculated feedback state
  const [feedback, setFeedback] = useState<MoveFeedbackType | null>(null);
  const [distance, setDistance] = useState<number | null>(null);
  const [animationKey, setAnimationKey] = useState(0);
  
  // Track whether to show countdown (for hint feedback two-phase display)
  const [showCountdown, setShowCountdown] = useState(false);

  // Calculate feedback when word data changes
  useEffect(() => {
    // Reset animation and recalculate when words change
    if (!word1Lemmatized || !word2Lemmatized || !goalWord || !startWord) {
      setFeedback(null);
      setDistance(null);
      return;
    }

    // Increment animation key to replay animation
    setAnimationKey(prev => prev + 1);

    const embeddingsManager = getEmbeddingsManagerInstance();
    const initialBfsDistance = optimalPath ? optimalPath.length - 1 : 10;
    
    logger.debug(`[Move Feedback] Calculating feedback: "${word1Lemmatized}" (BFS: ${word1BfsDistance}) → "${word2Lemmatized}" (BFS: ${word2BfsDistance}), embeddings loaded: ${!!embeddingsManager?.loaded}`);
    
    let calculatedFeedback: MoveFeedbackType;
    let percentageProgress: number | null = null;
    
    if (embeddingsManager && embeddingsManager.loaded) {
      const word1Similarity = embeddingsManager.similarity(word1Lemmatized, goalWord);
      const word2Similarity = embeddingsManager.similarity(word2Lemmatized, goalWord);
      
      if (word1Similarity !== null && word2Similarity !== null) {
        const startSim = embeddingsManager.similarity(startWord, goalWord) || 0;
        
        // Calculate blended percentage for both words
        const calculateBlendedPercentage = (similarity: number, bfsDistance: number | null | undefined): number => {
          const cosineProgress = (similarity - startSim) / (1 - startSim);
          let cosinePercentage = Math.max(0, Math.min(100, cosineProgress * 100));
          
          let bfsPercentage = 0;
          if (bfsDistance !== null && bfsDistance !== undefined && initialBfsDistance > 0) {
            const bfsProgress = (initialBfsDistance - bfsDistance) / initialBfsDistance;
            bfsPercentage = Math.max(0, Math.min(100, bfsProgress * 100));

            if (bfsDistance === 0) {
              // Perfect BFS match - override to 100%
              bfsPercentage = 100;
            } else if (bfsDistance === 1) {
              bfsPercentage = 95;
            } else if (bfsDistance === 2) {
              bfsPercentage = 85;
            }
          }
          
          // Blend cosine and BFS percentages
          // When very close (1-3 moves), use strongly-weighted average: BFS distance matters much more than cosine similarity
          // Otherwise, use more even weighting
          let blendedPercentage;
          if (bfsDistance !== null && bfsDistance !== undefined) {
            if (bfsDistance >= 1 && bfsDistance <= 2) {
              // Weighted average for proximity
              blendedPercentage = (bfsPercentage * 4 + cosinePercentage * 1) / 5;
            } else {
              // Normal blend
              blendedPercentage = (bfsPercentage * 2 + cosinePercentage * 1) / 3;
            }
          } else {
            blendedPercentage = cosinePercentage;
          }
          
          return blendedPercentage;
        };
        
        const word1Percentage = calculateBlendedPercentage(word1Similarity, word1BfsDistance);
        const word2Percentage = calculateBlendedPercentage(word2Similarity, word2BfsDistance);
        
        const percentageChange = word2Percentage - word1Percentage;
        const HOTTER_THRESHOLD = 30;  // Notable progress %
        const WARMER_THRESHOLD = 5;  // Slight progress %
        const COLDER_THRESHOLD = -5; // Slight regression %
        
        if (percentageChange > HOTTER_THRESHOLD) {
          calculatedFeedback = MoveFeedbackType.HOTTER;
        } else if (percentageChange > WARMER_THRESHOLD) {
          calculatedFeedback = MoveFeedbackType.WARMER;
        } else if (percentageChange < COLDER_THRESHOLD) {
          calculatedFeedback = MoveFeedbackType.COLDER;
        } else {
          calculatedFeedback = MoveFeedbackType.NO_CHANGE;
        }
        
        percentageProgress = Math.round(word2Percentage);
        logger.debug(`[Move Feedback] \"${word1Lemmatized}\" (${word1Percentage.toFixed(1)}%, BFS: ${word1BfsDistance}) → \"${word2Lemmatized}\" (${percentageProgress}%, BFS: ${word2BfsDistance}), change: ${percentageChange.toFixed(1)}%, feedback: ${calculatedFeedback}`);
      } else {
        // Fallback: one or both similarities missing
        logger.warn(`[Move Feedback] Embeddings loaded but missing similarity - word1Sim: ${word1Similarity !== null}, word2Sim: ${word2Similarity !== null}`);
        calculatedFeedback = getFeedbackFromBFS(word1BfsDistance, word2BfsDistance);
        if (word2Similarity !== null) {
          const startSim = embeddingsManager.similarity(startWord, goalWord) || 0;
          const progress = (word2Similarity - startSim) / (1 - startSim);
          percentageProgress = Math.round(Math.max(0, Math.min(100, progress * 100)));
          logger.debug(`[Move Feedback] BFS feedback: ${calculatedFeedback}, cosine percentage: ${percentageProgress}%`);
        } else if (word2BfsDistance !== null && word2BfsDistance !== undefined && initialBfsDistance > 0) {
          const bfsProgress = (initialBfsDistance - word2BfsDistance) / initialBfsDistance;
          percentageProgress = Math.round(Math.max(0, Math.min(100, bfsProgress * 100)));
          logger.debug(`[Move Feedback] BFS-only: ${calculatedFeedback}, BFS percentage: ${percentageProgress}%`);
        }
      }
    } else {
      // Fallback: embeddings not loaded at all
      logger.warn(`[Move Feedback] Embeddings manager not loaded - using BFS-only feedback`);
      calculatedFeedback = getFeedbackFromBFS(word1BfsDistance, word2BfsDistance);
      if (word2BfsDistance !== null && word2BfsDistance !== undefined && initialBfsDistance > 0) {
        const bfsProgress = (initialBfsDistance - word2BfsDistance) / initialBfsDistance;
        percentageProgress = Math.round(Math.max(0, Math.min(100, bfsProgress * 100)));
        logger.debug(`[Move Feedback] BFS-only: ${calculatedFeedback}, BFS percentage: ${percentageProgress}%`);
      }
    }
    
    setFeedback(calculatedFeedback);
    setDistance(percentageProgress);
  }, [word1Lemmatized, word2Lemmatized, word1BfsDistance, word2BfsDistance, goalWord, startWord, optimalPath]);

  // Helper function to get feedback based on BFS distance change
  const getFeedbackFromBFS = (previousDistance: number | null | undefined, currentDistance: number | null | undefined): MoveFeedbackType => {
    if (currentDistance === null || currentDistance === undefined) {
      logger.debug(`[BFS Feedback] Current distance null/undefined → DEAD_END`);
      return MoveFeedbackType.DEAD_END;
    }
    
    if (previousDistance === null || previousDistance === undefined) {
      logger.debug(`[BFS Feedback] Previous distance null/undefined → NO_CHANGE`);
      return MoveFeedbackType.NO_CHANGE;
    }
    
    const distanceChange = previousDistance - currentDistance;
    logger.debug(`[BFS Feedback] Distance change: ${previousDistance} → ${currentDistance} (Δ=${distanceChange})`);
    
    if (distanceChange > 1) {
      logger.debug(`[BFS Feedback] Δ > 1 → HOTTER`);
      return MoveFeedbackType.HOTTER;
    } else if (distanceChange > 0.05) {
      logger.debug(`[BFS Feedback] Δ > 0.05 → WARMER`);
      return MoveFeedbackType.WARMER;
    } else if (distanceChange < -0.05) {
      logger.debug(`[BFS Feedback] Δ < -0.05 → COLDER`);
      return MoveFeedbackType.COLDER;
    } else {
      logger.debug(`[BFS Feedback] -0.05 ≤ Δ ≤ 0.05 → NO_CHANGE`);
      return MoveFeedbackType.NO_CHANGE;
    }
  };

  // Play countdown sound when countdown phase starts (with a delay)
  useEffect(() => {
    if (showCountdown && feedback === MoveFeedbackType.HINT) {
      const soundTimer = setTimeout(() => {
        playSound(SharedSoundEffect.TIME_COUNTDOWN);
      }, 350); // Delay sound by 500ms after countdown appears
      
      return () => clearTimeout(soundTimer);
    }
  }, [showCountdown, feedback]);

  useEffect(() => {
    if (feedback) {
      // Reset countdown visibility when feedback changes
      setShowCountdown(false);
      
      // Play appropriate sound effect based on feedback type
      switch (feedback) {
        case MoveFeedbackType.HOTTER:
        case MoveFeedbackType.WARMER:
          playSound(warmerSound);
          break;
        case MoveFeedbackType.COLDER:
          playSound(colderSound);
          break;
        case MoveFeedbackType.NO_CHANGE:
          playSound(noChangeSound);
          break;
        case MoveFeedbackType.HINT:
          playSound(SharedSoundEffect.HINT);
          break;
        // Note: DEAD_END doesn't have a specific sound (could use NO_CHANGE or silence)
        default:
          break;
      }
      
      // For hint feedback, show deduction first, then countdown after 1 second
      if (feedback === MoveFeedbackType.HINT) {
        const countdownTimer = setTimeout(() => {
          setShowCountdown(true);
        }, 1000); // Show countdown after 1 second
        
        const completeTimer = setTimeout(() => {
          onComplete?.();
        }, 2800); // Total display time: 1s deduction + 1.8s countdown + linger
        
        return () => {
          clearTimeout(countdownTimer);
          clearTimeout(completeTimer);
        };
      } else {
        // For non-hint feedback, use original timing
        const timer = setTimeout(() => {
          onComplete?.();
        }, 2000);
        
        return () => clearTimeout(timer);
      }
    }
  }, [feedback, animationKey, onComplete]);

  const getFeedbackStyle = () => {
    switch (feedback) {
      case MoveFeedbackType.HOTTER:
        return {
          backgroundColor: '#FF8C00', // Dark orange (warm)
          icon: Lucide.Flame,
          text: 'Much warmer!'
        };
      case MoveFeedbackType.WARMER:
        return {
          backgroundColor: '#D2691E', // Chocolate/burnt orange (sepia palette)
          icon: Lucide.Flame,
          text: 'Warmer'
        };
      case MoveFeedbackType.COLDER:
        return {
          backgroundColor: '#5F9EA0', // Cadet blue (muted, sepia-friendly)
          icon: Lucide.Snowflake,
          text: 'Colder'
        };
      case MoveFeedbackType.NO_CHANGE:
        return {
          backgroundColor: '#9C8AA5', // Muted purple-gray
          icon: Lucide.EqualApproximately,
          text: 'Same'
        };
      case MoveFeedbackType.DEAD_END:
        return {
          backgroundColor: '#D9534F', // Clear red
          icon: Lucide.XCircle,
          text: 'Dead End!'
        };
      case MoveFeedbackType.HINT:
        return {
          backgroundColor: gameColor,
          icon: Lucide.Lightbulb,
          text: null // We'll render the counter separately
        };
      default:
        return {
          backgroundColor: '#95a5a6',
          icon: Lucide.HelpCircle,
          text: feedback
        };
    }
  };

  const { backgroundColor, icon: Icon, text } = getFeedbackStyle();

  // Format distance text
  const getDistanceText = () => {
    // Don't show distance text for hint feedback (it's already in the main text)
    if (feedback === MoveFeedbackType.HINT) return null;
    
    if (distance === null || distance === undefined) return null;
    
    // Distance is now a percentage (0-100)
    return `${distance}% hot`;
  };

  const distanceText = getDistanceText();

  return (
    <AnimatePresence mode="wait">
      {feedback && (
        <motion.div
          key={animationKey}
          initial={{ 
            opacity: 0, 
            y: 200,
            x: '-50%'
          }}
          animate={{ 
            opacity: 1, 
            y: 0,
            x: '-50%'
          }}
          exit={{ 
            opacity: 0, 
            y: 200,
            x: '-50%'
          }}
          transition={{ duration: 0.3 }}
          onClick={() => onComplete?.()}
          style={{
            position: 'fixed',
            bottom: '5rem',
            left: '50%',
            backgroundColor,
            color: 'white',
            padding: '0.75rem 2rem',
            borderRadius: '1rem',
            fontSize: '1.6rem',
            fontWeight: 'bold',
            fontFamily: game.fontSecondary,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            zIndex: 9999,
            textAlign: 'center',
            minWidth: '200px',
            cursor: 'pointer',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.25rem' }}>
            <Icon size={32} strokeWidth={2} />
          </div>
          <div style={{ position: 'relative', minHeight: '2rem' }}>
            {feedback === MoveFeedbackType.HINT ? (
              <>
                <AnimatePresence mode="wait">
                  {!showCountdown ? (
                    // Phase 1: Show time deduction for 1 second with animations
                    typeof distance === 'number' && typeof timeAfter === 'number' && distance > timeAfter && (
                      <motion.div
                        key="deduction"
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -50 }}
                        transition={{ duration: 0.3 }}
                        style={{ 
                          fontSize: '1.6rem', 
                          fontWeight: 'bold',
                        }}
                      >
                        -{distance - timeAfter} sec
                      </motion.div>
                    )
                  ) : (
                    // Phase 2: Show countdown animation after 1 second with entrance animation
                    <motion.div
                      key="countdown"
                      initial={{ opacity: 0, x: 50 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3 }}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                    >
                      <TimeCountdown 
                        start={distance || 0}
                        end={timeAfter || 0}
                        duration={0.6}
                      />
                      <span style={{ fontSize: '1.2rem' }}>left</span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            ) : (
              text
            )}
          </div>
          {distanceText && (
            <div style={{ 
              fontSize: '1rem', 
              marginTop: '0.25rem',
              fontWeight: 'normal',
              opacity: 0.9
            }}>
              {distanceText}
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
